import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import CidesLogo from "@/components/CidesLogo";

const NAV_ITEMS = [
  { to: "/", key: "nav.home" },
  { to: "/documents", key: "nav.documents" },
  { to: "/telecom", key: "nav.telecom" },
  { to: "/finance", key: "nav.finance" },
];

// Shared focus-visible utility — visible ONLY on real keyboard focus
const FV = "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2AB0EA]";

export const Nav = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0f1826]/85 border-b border-white/[0.08]"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" className={`flex items-center gap-3 group ${FV}`} data-testid="nav-logo">
          <CidesLogo height={34} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.to === "/" ? "home" : item.to.slice(1)}-link`}
              className={({ isActive }) =>
                [
                  "nav-link relative py-1 text-[13px] uppercase tracking-[0.16em] font-medium",
                  "transition-colors whitespace-nowrap",
                  isActive ? "nav-link--active text-white" : "text-slate-300 hover:text-white",
                  FV,
                ].join(" ")
              }
            >
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="hidden sm:flex items-center border border-white/10 text-[11px] uppercase tracking-[0.2em] font-medium"
            data-testid="lang-switch"
          >
            <button
              type="button"
              onClick={() => setLang("fr")}
              data-testid="lang-fr"
              aria-pressed={lang === "fr"}
              className={`lang-btn px-2.5 py-1.5 transition-colors ${FV} ${
                lang === "fr" ? "is-active bg-[#0089C8] text-white" : "text-slate-300 hover:text-white"
              }`}
            >
              FR
            </button>
            <button
              type="button"
              onClick={() => setLang("en")}
              data-testid="lang-en"
              aria-pressed={lang === "en"}
              className={`lang-btn px-2.5 py-1.5 transition-colors ${FV} ${
                lang === "en" ? "is-active bg-[#0089C8] text-white" : "text-slate-300 hover:text-white"
              }`}
            >
              EN
            </button>
          </div>

          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            className={`hidden md:inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-5 py-2 text-[11px] uppercase tracking-[0.14em] font-semibold transition-colors whitespace-nowrap shrink-0 leading-none ${FV}`}
          >
            {t("nav.contactCta")}
          </Link>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden text-white p-2 ${FV}`}
            data-testid="nav-mobile-toggle"
            aria-label="menu"
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/[0.08] bg-[#0f1826]" data-testid="mobile-nav">
          <div className="px-6 py-4 flex flex-col gap-4">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `nav-link text-sm uppercase tracking-[0.2em] ${FV} ${
                    isActive ? "nav-link--active text-white" : "text-slate-300"
                  }`
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
            <NavLink
              to="/contact"
              className={`text-sm uppercase tracking-[0.2em] text-[#FF6B35] ${FV}`}
            >
              {t("nav.contactCta")}
            </NavLink>
            <div className="flex items-center gap-2 mt-2 border-t border-white/[0.08] pt-4">
              <button
                type="button"
                onClick={() => setLang("fr")}
                aria-pressed={lang === "fr"}
                className={`px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase border border-white/10 ${FV} ${
                  lang === "fr" ? "bg-[#0089C8] text-white" : "text-slate-300"
                }`}
              >
                FR
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                aria-pressed={lang === "en"}
                className={`px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase border border-white/10 ${FV} ${
                  lang === "en" ? "bg-[#0089C8] text-white" : "text-slate-300"
                }`}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Nav;
